import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { mapProps } from '../../../../../../../util/hocs';
import { createLoadMoreMethod } from '../../../../../../../util/apollo';
import MembershipsNavigationTrigger from './MembershipsNavigationTrigger';
import useExpanded from '../../../../../hooks/useExpanded';
import getAlbumMembershipGroupNavigation, {
  getAlbumMembershipGroupRoot,
} from '../../graphql/queries/getAlbumMembershipGroupNavigation';
import { getAlbumMembershipGroupRootId } from './helpers';
import MembershipsNavigationLink from './MembershipsNavigationLink';
import ScrollableDiv from '../../../../../../../components/global/ScrollableDiv';
import Angle from '../../../../../../../components/global/Angle';
import ScrollLoader from '../../../../../../../components/global/ScrollLoader';
import getHierarchyItemHierarchyBreadcrumb from '../../graphql/queries/getHierarchyItemHierarchyBreadcrumb';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  rootAlbumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
    album_memberships_collection: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          matched_photos_extended: PropTypes.shape({
            total: PropTypes.number,
          }),
          album: PropTypes.shape({
            name: PropTypes.string,
          }),
        }),
      ),
    }),
  }),
  loadMore: PropTypes.func,
  loading: PropTypes.bool,
  loadingAlbum: PropTypes.bool,
  loadingAlbumFolder: PropTypes.bool,
};

const MembershipsNavigation = ({
  albumMembershipGroup,
  album,
  albumFolder,
  rootAlbumMembershipGroup,
  loadMore,
  loading,
  loadingAlbum,
  loadingAlbumFolder,
  location,
}) => {
  const triggerRef = useRef();
  const scrollRef = useRef();
  const { expanded, setExpanded } = useExpanded(triggerRef);

  if (loading || loadingAlbum || loadingAlbumFolder) {
    return <MembershipsNavigationTrigger title="Loading..." loading />;
  }

  const entity = album ?? albumFolder;
  const entityHasHierarchy =
    entity?.album_folder_hierarchy?.records?.length > 0 &&
    !entity?.album_folder?.individual_albums;
  const displayName = rootAlbumMembershipGroup?.display_name ?? entity?.name;
  const albumMemberships =
    rootAlbumMembershipGroup?.album_memberships_collection ?? [];
  const currentAlbum = albumMembershipGroup?.album;
  // Assume All Photos if there's no membership.
  const isAllPhotosUrl = !albumMembershipGroup
    ? true
    : location?.pathname ===
      `/consumer/matches/${albumMembershipGroup?.id}/all`;
  const name = currentAlbum?.name;
  const dropdownClasses = classNames({
    'membership-nav-dropdown': true,
    'is-expanded': expanded,
  });

  return (
    <div className="membership-nav">
      <MembershipsNavigationTrigger
        title={displayName}
        // Don't show a subtitle if the displayName is the same as the name
        // (mostly true for individual albums).
        subtitle={name === displayName ? '' : name}
        hasChildren={false}
        onClick={() => {}}
        expanded={expanded}
        ref={triggerRef}
      />

      {(albumMemberships.total > 1 ||
        (albumMembershipGroup &&
          rootAlbumMembershipGroup &&
          entityHasHierarchy)) && (
        <div className={dropdownClasses}>
          {albumMembershipGroup.id !== rootAlbumMembershipGroup.id ? (
            <MembershipsNavigationLink
              label={
                <span className="d-flex align-items-center">
                  <Angle direction="left" className="mr-1" /> All Albums
                </span>
              }
              id={rootAlbumMembershipGroup.id}
              onClick={() => setExpanded(false)}
              className="membership-nav-link-all"
              toAllPhotos={isAllPhotosUrl}
            />
          ) : (
            <MembershipsNavigationLink
              label={
                <span className="text-color-base">
                  Show matched photos in...
                </span>
              }
              id={rootAlbumMembershipGroup.id}
              className="membership-nav-link-all"
              toAllPhotos={isAllPhotosUrl}
            />
          )}

          <ScrollableDiv maxSize="240px" ref={scrollRef}>
            {albumMemberships.records
              .filter(
                albumMembership =>
                  albumMembership.album.id !== currentAlbum?.id,
              )
              .map(albumMembership => {
                return (
                  <MembershipsNavigationLink
                    key={albumMembership.id}
                    label={albumMembership.album.name}
                    id={albumMembership.album.id}
                    onClick={() => setExpanded(false)}
                    count={albumMembership.matched_photos_extended.total}
                    totalPhotos={albumMembership.album?.photos?.total}
                    toAllPhotos={isAllPhotosUrl}
                  />
                );
              })}
          </ScrollableDiv>

          <ScrollLoader
            // TODO @consumer: Replace this component with a better version (hooks)
            loadMore={loadMore}
            shouldLoadMore={
              albumMemberships.total > albumMemberships.records.length
            }
            target={scrollRef?.current}
          />
        </div>
      )}
      {!albumMembershipGroup && entityHasHierarchy && (
        <div className={dropdownClasses}>
          <ScrollableDiv maxSize="240px" ref={scrollRef}>
            {entity.album_folder_hierarchy.records
              .filter(Boolean)
              .map(hierarchy => {
                return (
                  <MembershipsNavigationLink
                    key={hierarchy.id}
                    label={hierarchy.name}
                    id={hierarchy.id}
                    onClick={() => setExpanded(false)}
                    toAllPhotos
                  />
                );
              })}
          </ScrollableDiv>
        </div>
      )}
    </div>
  );
};

MembershipsNavigation.propTypes = propTypes;

export { MembershipsNavigation as MembershipsNavigationComponent };

export default compose(
  withRouter,
  mapProps(props => ({
    ...props,
    albumMembershipGroupId: props.match.params.albumMembershipGroupId,
  })),
  getAlbumMembershipGroupRoot,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroupId: getAlbumMembershipGroupRootId(
      data?.albumMembershipGroup,
    ),
    albumMembershipGroup: data?.albumMembershipGroup,
  })),
  getAlbumMembershipGroupNavigation,
  mapProps(({ data, ...props }) => ({
    ...props,
    rootAlbumMembershipGroup: data?.albumMembershipGroup,
    loading: data?.loading,
    loadMore: createLoadMoreMethod({
      source: data?.albumMembershipGroup,
      recordsPath: 'album_memberships_collection',
      fetchMore: data?.fetchMore,
    }),
  })),
  getHierarchyItemHierarchyBreadcrumb,
  mapProps(({ data, ...props }) => ({
    ...props,
    album: data?.hierarchyItem?.album,
    albumFolder: data?.hierarchyItem?.album_folder,
    loadingAlbum: data?.loading,
    loadingAlbumFolder: data?.loading,
  })),
)(MembershipsNavigation);
